/**
 * Servicio de validación de turnos para guardias (rol 9)
 * Maneja restricciones de duración máxima y control de acceso
 */

// Constante: máximo de horas permitidas en un turno (24 horas)
export const MAX_SHIFT_HOURS = 24
export const MAX_SHIFT_DURATION_MS = MAX_SHIFT_HOURS * 60 * 60 * 1000

/**
 * Calcula el tiempo transcurrido desde el inicio de un turno
 * @param {string|Date} startTime - Hora de inicio del turno (ISO 8601)
 * @returns {number} - Milisegundos transcurridos
 */
export function getElapsedTimeMs(startTime) {
  if (!startTime) return 0
  const start = new Date(startTime).getTime()
  const now = new Date().getTime()
  return Math.max(0, now - start)
}

/**
 * Obtiene el tiempo restante antes de llegar al límite de 24 horas
 * @param {string|Date} startTime - Hora de inicio del turno
 * @returns {number} - Milisegundos restantes (0 si ya pasó el límite)
 */
export function getRemainingTimeMs(startTime) {
  const elapsed = getElapsedTimeMs(startTime)
  const remaining = MAX_SHIFT_DURATION_MS - elapsed
  return Math.max(0, remaining)
}

/**
 * Verifica si un guardia ha excedido el límite de 24 horas
 * @param {string|Date} startTime - Hora de inicio del turno
 * @returns {boolean} - true si excedió, false en caso contrario
 */
export function hasExceededMaxShiftDuration(startTime) {
  return getElapsedTimeMs(startTime) > MAX_SHIFT_DURATION_MS
}

/**
 * Obtiene el tiempo como string formateado (ej: "5h 30m")
 * @param {number} milliseconds - Tiempo en milisegundos
 * @returns {string} - Tiempo formateado
 */
export function formatDuration(milliseconds) {
  const totalSeconds = Math.floor(milliseconds / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  } else {
    return `${seconds}s`
  }
}

/**
 * Verifica si un guardia puede realizar acciones (debe tener turno activo)
 * @param {Object} activeShift - Datos del turno activo del guardia
 * @returns {Object} - { canPerformActions: boolean, message: string, timeExceeded: boolean }
 */
export function validateGuardCanPerformActions(activeShift) {
  if (!activeShift) {
    return {
      canPerformActions: false,
      message: 'No tienes un turno activo. Debes iniciar un turno para realizar acciones.',
      timeExceeded: false
    }
  }

  const elapsed = getElapsedTimeMs(activeShift.start_time)
  const timeExceeded = hasExceededMaxShiftDuration(activeShift.start_time)

  if (timeExceeded) {
    return {
      canPerformActions: false,
      message: `Tu turno ha excedido el límite máximo de ${MAX_SHIFT_HOURS} horas. Tu sesión será cerrada automáticamente.`,
      timeExceeded: true
    }
  }

  return {
    canPerformActions: true,
    message: '',
    timeExceeded: false
  }
}

/**
 * Obtiene información de alerta para mostrar al guardia
 * @param {Object} activeShift - Datos del turno activo
 * @returns {Object|null} - { type: 'warning'|'error', message: string } o null
 */
export function getShiftAlertInfo(activeShift) {
  if (!activeShift) return null

  const elapsed = getElapsedTimeMs(activeShift.start_time)
  const exceeded = hasExceededMaxShiftDuration(activeShift.start_time)

  if (exceeded) {
    return {
      type: 'error',
      message: `⚠️ Tu turno ha excedido ${MAX_SHIFT_HOURS} horas. Tu sesión se cerrará automáticamente.`
    }
  }

  // Alerta si quedan menos de 30 minutos
  if (elapsed > MAX_SHIFT_DURATION_MS - (30 * 60 * 1000)) {
    const remaining = getRemainingTimeMs(activeShift.start_time)
    const formattedTime = formatDuration(remaining)
    return {
      type: 'warning',
      message: `⏰ Tu turno vence en ${formattedTime}. Ciérralo o tu sesión se cerrará automáticamente.`
    }
  }

  return null
}

/**
 * Valida si un guardia puede iniciar un nuevo turno
 * @param {Object} currentShift - Turno actual/activo del guardia
 * @returns {Object} - { canStart: boolean, message: string }
 */
export function validateCanStartShift(currentShift) {
  if (!currentShift) {
    return {
      canStart: true,
      message: ''
    }
  }

  if (hasExceededMaxShiftDuration(currentShift.start_time)) {
    return {
      canStart: true,
      message: `Tu turno anterior excedió ${MAX_SHIFT_HOURS} horas. Puedes iniciar un nuevo turno.`
    }
  }

  return {
    canStart: false,
    message: `Ya tienes un turno activo. Debes cerrarlo antes de iniciar uno nuevo.`
  }
}
